import React, { useContext, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ApiContext } from '@api/api';
import * as yup from 'yup';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import useBEValidation from '@hooks/useBEValidation/useBEValidation.tsx';
import { CameraTypeEnum, CompanyModuleEnum } from '@api/ringil-api.v2.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputCompanyLocation from '@components/obsolete/Form/InputEntityOwner/InputCompanyLocation.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { findValidationMessage } from '@utils/validation.tsx';

interface ComponentProps {
  onSaved: () => void;
}

interface Form {
  companyLocationId: string;
  externalCamId: string;
  carPlate: CameraTypeEnum;
}

export const useAdminAddCarPlateSidebar = () => {
  const [adminAddCarPlateSidebarVisible, setAdminAddCarPlateSidebarVisible] = useState(false);

  const AdminAddCarPlateSidebar: React.FC<ComponentProps> = ({ onSaved }) => {
    const { adminCameraOpen } = useContext(ApiContext);
    const { tr } = useTranslationLgs();
    const { setBeValidationErrors, BackendValidationComponent } = useBEValidation();
    const { loggedUser } = useContext(AuthenticatedUserContext);

    const form = useForm<Form>(
      yup.object().shape({
        companyLocationId: yup.string().required().min(1),
        externalCamId: yup.string().required().min(1),
        carPlate: yup.string().required().min(1),
      }),
      {
        companyLocationId: null,
        externalCamId: null,
        carPlate: null,
      },
      d => save(d),
      false,
      false,
    );

    const save = (data: Form) => {
      adminCameraOpen(data.companyLocationId,
        {
          externalCamId: data.externalCamId,
          carPlate: data.carPlate,
        },
        () => {
          onSaved();
          setAdminAddCarPlateSidebarVisible(false);
        },
        null,
        {
          onValidationFailed: setBeValidationErrors,
        },
      );
    };

    return (
      <>
        <RingilSidebar
          visible={adminAddCarPlateSidebarVisible}
          onContinue={() => {
            form.validateAndSend();
          }}
          onBack={() => {
            setAdminAddCarPlateSidebarVisible(false);
          }}
          heading={tr('AdminAddCarPlateSidebar.addCarplate', 'Open gate for car plate')}
        >
          <div className={'flex flex-col gap-4'}>
            <BackendValidationComponent />
            <Field
              label={tr('AdminAddCarPlateSidebar.location', 'Location')}
              required={true}
              errorMessage={findValidationMessage(form.validationErrors, form.names.companyLocationId, tr)}
            >
              <InputCompanyLocation
                companyLocationId={form.values.companyLocationId}
                companyId={loggedUser.companyId}
                onChange={v => form.setFieldValue(form.names.companyLocationId, v?.id)}
                moduleName={CompanyModuleEnum.TIMESLOTS}
                filterEnabled={true}
              />
            </Field>
            <Field
              label={tr('AdminAddCarPlateSidebar.externalCamId', 'External camera ID')}
              required
              errorMessage={findValidationMessage(form.validationErrors, form.names.externalCamId, tr)}
            >
              <RingilInputText value={form.values.externalCamId}
                               onChange={v => form.setFieldValue(form.names.externalCamId, v)} />
            </Field>
            <Field
              label={tr('AdminAddCarPlateSidebar.carPlate', 'Car plate')}
              required
              errorMessage={findValidationMessage(form.validationErrors, form.names.carPlate, tr)}
            >
              <RingilInputText value={form.values.carPlate}
                               onChange={v => form.setFieldValue(form.names.carPlate, v)} />
            </Field>
          </div>
        </RingilSidebar>
      </>
    );
  };

  return { AdminAddCarPlateSidebar, adminAddCarPlateSidebarVisible, setAdminAddCarPlateSidebarVisible };
};
